import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';

import { i18n } from '@apps/main/modules/localization/localizationSetup';
import type { PriceRibbonMonth } from '@apps/main/new-desktop/modules/priceRibbon';
import type { PriceCalendarState } from '@apps/main/new-desktop/modules/priceCalendar';

import type { AppSingleSelectOption } from '@libs/ui';
import { isFeatureToggleActive } from '@libs/feature-toggle';
import { DateFormat, weekdays } from '@libs/sembo-dates';

import { useBrowserStore } from '../browserStore';
import { useStatsigEventsStore } from '../statsigEvents/statsigEventsStore';
import type { CoreEventSource } from '../../models';
import { useTripStore } from '../trip/tripStore';
import { useItineraryStore } from '../itinerary/itineraryStore';
import { usePreferredTimeStore } from '../preferredTimeStore';
import { useGlobalCurrencyStore } from '../globalCurrencyStore';

import {
  getPriceRibbonMonths,
  getSelectedMonthOption,
  getMonthsSelectOptions,
  getPriceCalendarState,
  getCalendarTriggerText,
} from './priceCalendarStoreLogic';

export const usePriceCalendarStore = defineStore('PriceCalendarStore', () => {
  const { t } = i18n.global;

  const tripStore = useTripStore();
  const browserStore = useBrowserStore();
  const itineraryStore = useItineraryStore();
  const preferredTimeStore = usePreferredTimeStore();
  const statsigEventsStore = useStatsigEventsStore();
  const globalCurrencyStore = useGlobalCurrencyStore();

  // State
  const showPriceRibbonPulsing = ref<boolean>(false);
  let isFirstLoad = true;

  // Actions
  function selectItineraryDate(date: Date, source: CoreEventSource): void {
    statsigEventsStore.logCoreDateChanged({ source });
    itineraryStore.toggleItinerary(date);
  }

  function selectItineraryMonth(date: Date, source: CoreEventSource): void {
    statsigEventsStore.logCoreDateChanged({ source });
    tripStore.toggleExpandedMonth(date);
  }

  function showPulsingIndicator(): void {
    showPriceRibbonPulsing.value = true;
  }

  function hidePulsingIndicator(): void {
    showPriceRibbonPulsing.value = false;
  }

  // Getters
  const allowHoverActions = computed(() => !browserStore.isSmallerThan1200px);

  const priceRibbonMonths = computed<PriceRibbonMonth[]>(() =>
    getPriceRibbonMonths({
      months: tripStore.months,
      itineraryDates: itineraryStore.itineraryDates,
      isSearchingItineraries: itineraryStore.isSearchingItineraries,
      isSelectedItinerary: itineraryStore.isSelectedItinerary,
      isDateInExpandedMonth: preferredTimeStore.isDateInExpandedMonth,
      selectedItinerary: itineraryStore.selectedItinerary,
      moneyFormanterFn: globalCurrencyStore.formatMoney,
    })
  );

  const priceCalendarState = computed<PriceCalendarState>(() =>
    getPriceCalendarState({
      weekdays: weekdays(DateFormat.ShortDayName),
      itineraryDates: itineraryStore.itineraryDates,
      alternativeProductMessage: t('planner.alternative_avail'),
      noAvailabilityMessage: t('planner.no_avail'),
      forceLoader: isFeatureToggleActive('loader'),
      moneyFormanterFn: globalCurrencyStore.formatWithoutCurrency,
      selectedDate: preferredTimeStore.preferredDay,
      suggestedDate: itineraryStore.cheapestItinerary?.date,
    })
  );

  const monthsSelectOptions = computed<AppSingleSelectOption<string>[]>(() =>
    getMonthsSelectOptions(tripStore.months)
  );

  const selectedMonthOption = computed<AppSingleSelectOption<string>>(() =>
    getSelectedMonthOption(
      monthsSelectOptions.value,
      preferredTimeStore.isDateInExpandedMonth
    )
  );

  const calendarTriggerText = computed<string>(() =>
    getCalendarTriggerText(
      preferredTimeStore.preferredDay,
      selectedMonthOption.value
    )
  );

  // Watchers

  watch(
    () => itineraryStore.isNoAvailForMonth,
    () => {
      if (itineraryStore.isNoAvailForMonth) {
        statsigEventsStore.logNoAvailForMonth(isFirstLoad);
      }
      if (itineraryStore.isNoAvailForMonth === undefined) {
        isFirstLoad = false;
      }
    }
  );

  return {
    // State
    showPriceRibbonPulsing,

    // Actions
    selectItineraryDate,
    selectItineraryMonth,
    showPulsingIndicator,
    hidePulsingIndicator,

    // Getters
    allowHoverActions,
    priceRibbonMonths,
    priceCalendarState,
    selectedMonthOption,
    monthsSelectOptions,
    calendarTriggerText,
  };
});
